import React, { useEffect } from 'react';
import {
  Box,
  FlexBox,
  Typography,
  Card,
  color,
  Button,
  InputField,
  SvgIconButton,
  Grid,
  MuiBox,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { isToday } from 'date-fns';
import Img from 'react-image';
import hash from 'object-hash';
import { DataEntryOverlay } from 'components/DataEntryOverlay';
import { actions as ticketingActions, selectors as ticketingSelectors, Event } from 'services/ticketing';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import {
  selectors as configurationSelectors,
  Configuration,
  EventDisplaySettings,
  Message
} from 'services/configuration';
import { selectors as mediaSelectors, MediaResponse } from 'services/media';
import WillCallLogo from 'assets/will_call_icon.png';
import SearchIcon from 'assets/search_icon.png';
import EmptyTicketIcon from 'assets/tickets_icon.png';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { CalendarOverlay } from 'components/CalendarOverlay';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import moment, { Moment } from 'moment';
import { useStyles, HiddenButton } from './styled';
import { Paginator } from 'components/Paginator';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { selectors as cartSelectors } from 'services/cart';

const EventsView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { from?: string };

  // Action Creators and Selectors
  const { getEvents, setEventsConfig, setUpsell } = useActions({
    getEvents: ticketingActions.getEvents,
    setEventsConfig: ticketingActions.setEventsConfig,
    setUpsell: ticketingActions.setUpsell
  });
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const configuration = useSelector(configurationSelectors.selectConfiguration) as Configuration;
  const eventImages = useSelector(mediaSelectors.selectEventImages) || [];
  const events = useSelector(ticketingSelectors.selectEvents);
  const { page, selected, isAutoLaunch } = useSelector(ticketingSelectors.selectEventsConfig);
  const { selected: selectedGenre } = useSelector(ticketingSelectors.selectGenresConfig);
  const loading = useSelector(ticketingSelectors.selectLoading);
  const fetchCompleted = useSelector(ticketingSelectors.selectFetchCompleted);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const upsell = useSelector(ticketingSelectors.selectUpsell);
  const membershipProductInCart = useSelector(cartSelectors.selectMembershipProductInCart);
  const donationProductInCart = useSelector(cartSelectors.selectDonationProductInCart);
  const cart = useSelector(cartSelectors.selectCart);
  const cartItemIds = useSelector(cartSelectors.selectCartItemIds);

  const { clientId } = kiosk.venue;
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/event_images/`;
  const autoSelectEvent = get(configuration, `${ticketingPath}.autoSelectEvent`);
  const filterByName = get(configuration, `${ticketingPath}.filterByName`);
  const filterByDate = get(configuration, `${ticketingPath}.filterByDate`);
  const todayEventFlag = get(configuration, `${ticketingPath}.todayEventFlag`);
  const souvenirTicketEnabled = get(configuration, `${ticketingPath}.souvenirTicket.enabled`, false);
  const eventDisplaySettingsList: EventDisplaySettings[] = get(
    configuration,
    `${ticketingPath}.eventDisplaySettingsList`,
    []
  );
  const productsToExcludeFromProductSelection: string[] = get(
    configuration,
    `${ticketingPath}.productAvailabilityAndSelection.productsToExcludeFromProductSelection`,
    []
  );
  const messaging: Message = get(configuration, `messaging.${i18n.language}.eventsPage`, {});
  const touchForward = get(configuration, 'configuration.globalSettings.touchForward.purchaseFlow.enabled');
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const fonts = get(configuration, 'configuration.globalSettings.fonts');

  const [selectedEvent, setSelectedEvent] = React.useState<string>(touchForward ? '' : selected);
  const [currentPage, setCurrentPage] = React.useState(page);
  const [showNameSearchOverlay, setShowNameSearchOverlay] = React.useState<boolean>(false);
  const [showDateOverlay, setShowDateOverlay] = React.useState<boolean>(false);
  const [nameFilter, setNameFilter] = React.useState<boolean>(false);
  const [dateFilter, setDateFilter] = React.useState<boolean>(false);
  const [filteredEvents, setFilteredEvents] = React.useState<Event[]>([]);
  const [searchScript, setSearchScript] = React.useState<string>('');
  const [searchDateStart, setSearchDateStart] = React.useState<Moment>(moment());
  const [searchDateEnd, setSearchDateEnd] = React.useState<Moment>(moment());
  const [isSingleEvent, setSingleEvent] = React.useState<boolean>(false);
  const [uiLoading, setUILoading] = React.useState(true);
  const [hiddenButtonClicked, setHiddenButtonClicked] = React.useState<boolean>(false);
  const pinClearPointer = searchScript.length < 1 ? 'none' : 'auto';
  const transformTransition = 'opacity 250ms linear';
  const pinOpacity = searchScript.length < 1 ? 0 : 1;
  const dateOpacity = dateFilter ? 1 : 0;
  const dateClearPointer = dateFilter ? 'auto' : 'none';

  const classes = useStyles();

  // on mount gets the events needed for the page
  useEffect(() => {
    if (!fetchCompleted.events) getEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const genreFilteredList = getFilteredEvents();
    if (genreFilteredList && (isAutoLaunch || (autoSelectEvent && genreFilteredList.length === 1))) {
      const followingPages = ['/ticketing/tiers', '/ticketing/membership-level-selection', '/ticketing/donation'];
      if (state && followingPages.includes(state?.from ?? '')) {
        onPrevPageClick();
      } else {
        setSingleEvent(true);
        setSelectedEvent(isAutoLaunch ? selected : genreFilteredList[0].id);
      }
    } else {
      if (fetchCompleted.events) {
        setUILoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, fetchCompleted.events]);

  useEffect(() => {
    if (selectedEvent && (isSingleEvent || touchForward || hiddenButtonClicked)) {
      onNextPageClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, isSingleEvent, touchForward, hiddenButtonClicked]);

  useEffect(() => {
    getFilteredEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter, dateFilter, searchScript, searchDateStart, searchDateEnd]);

  // ***************
  // ** FUNCTIONS **
  // ***************
  const onPrevPageClick = () => {
    // sets the event page config to have nothing selected and be on first page
    setEventsConfig({ page: 1, selected: '' });

    // re-routes to the previous page (GenresView)
    navigate('/ticketing/genres', { state: { from: location.pathname } });
  };

  const dateTextForDisplay = () => {
    if (dateFilter) {
      return `${t('{{date, monthDay}}', { date: searchDateStart.toDate() })} - ${t('{{date, monthDay}}', {
        date: searchDateEnd.toDate()
      })}`;
    }
    return '';
  };

  const getFilteredEvents = () => {
    if (events && events.length) {
      let filteredEventList = events;

      // filter out products that are in this exclusion list
      if (productsToExcludeFromProductSelection.length) {
        filteredEventList = filteredEventList.filter((event) => {
          return !productsToExcludeFromProductSelection.includes(event.name);
        });
      }

      // filter by genre if one is selected
      if (selectedGenre) {
        filteredEventList = filteredEventList.filter((e) => e.genre === selectedGenre);
      }

      if (nameFilter) {
        filteredEventList = getEventsForName(filteredEventList, searchScript);
      }

      if (dateFilter) {
        filteredEventList = getEventsInDateRange(filteredEventList, searchDateStart, searchDateEnd);
      }

      // if a membership is in the cart: filter out products of that type
      if (membershipProductInCart && membershipProductInCart.type) {
        filteredEventList = filteredEventList.filter((p) => p.type !== membershipProductInCart.type);
      }
      // if a donation is in the cart: filter out products of that type
      if (donationProductInCart) {
        filteredEventList = filteredEventList.filter((p) => p.type !== donationProductInCart.type);
      }

      setFilteredEvents(filteredEventList);
      return filteredEventList;
    }
    return undefined;
  };

  const filterEventsByDate = (startDate: Moment, endDate: Moment) => {
    setShowDateOverlay(false);
    setCurrentPage(1);
    setSelectedEvent('');
    setSearchDateStart(startDate);
    setSearchDateEnd(endDate);
    setDateFilter(true);
  };

  const getEventsForName = (eventList: any[], value: string) => {
    return eventList.filter((e) => e.name.toLowerCase().includes(value.toLowerCase()));
  };

  const getEventsInDateRange = (eventList: any[], startDate: Moment, endDate: Moment) => {
    return eventList.filter((event) =>
      event.firstDate && event.lastDate
        ? moment(event.firstDate).isSameOrBefore(endDate.startOf('day')) &&
          moment(event.lastDate).isSameOrAfter(startDate.endOf('day'))
        : moment(event.date).isSameOrAfter(startDate.startOf('day')) &&
          moment(event.date).isSameOrBefore(endDate.endOf('day'))
    );
  };

  const filterEventsByName = (value: string) => {
    setCurrentPage(1);
    setSelectedEvent('');
    setShowNameSearchOverlay(false);
    setSearchScript(value);
    setNameFilter(true);
  };

  const resetNameFilter = () => {
    setCurrentPage(1);
    setSelectedEvent('');
    setSearchScript('');
    setNameFilter(false);
  };

  const resetDateFilter = () => {
    setCurrentPage(1);
    setSelectedEvent('');
    setDateFilter(false);
  };

  /**
   * Save initial product data for later use in the upsell flow.
   * - itemIdsInCartBeforeSelection is used to determine which items to keep
   * in the cart when navigating back from the upsell selection view.
   * - itemQtyInCartBeforeSelection is used to determine how many items
   * of the same product were in the cart before the upsell flow started,
   * and if the number of items to initiate the upsell prompt was crossed.
   */
  const saveDataForUpsell = (event: Event) => {
    let itemQtyInCartForProduct = 0;
    if (cart && cart.products) {
      itemQtyInCartForProduct = cart.products.reduce((count, cartProduct) => {
        return cartProduct.name === event.name ? count + cartProduct.quantity : count;
      }, 0);
    }

    setUpsell({
      ...upsell,
      initialProduct: {
        eventsConfig: { page: currentPage, selected: selectedEvent, isAutoLaunch },
        itemIdsInCartBeforeSelection: cartItemIds,
        itemQtyInCartBeforeSelection: itemQtyInCartForProduct
      }
    });
  };

  const onNextPageClick = () => {
    // if no event is selected, the action will not fire
    if (selectedEvent.length) {
      const event = filteredEvents.find((e) => e.id === selectedEvent) as Event;

      if (!isAutoLaunch) {
        // Only do this if it was not set in GenresView (indicated by isAutoLaunch)
        setEventsConfig({ page: currentPage, selected: selectedEvent });
      }
      saveDataForUpsell(event);

      // use the selected event type to determine where to navigate to
      let pathname = '/ticketing/tiers';
      if (hiddenButtonClicked) {
        pathname = '/ticketing/seat-selection';
      } else if (souvenirTicketEnabled) {
        pathname = '/ticketing/souvenir-ticket';
      } else if (event && event.type && event.type === 'membership') {
        pathname = '/ticketing/membership-level-selection';
      } else if (event && event.type && event.type === 'donation') {
        pathname = '/ticketing/donation';
      }

      // re-routes to the next page
      navigate(pathname, { state: { from: location.pathname } });
    }
  };

  const onHiddenButtonClick = (eventId: string) => {
    setHiddenButtonClicked(true);
    setSelectedEvent(eventId);
  };

  const resetFilters = () => {
    setNameFilter(false);
    setSearchScript('');
    setSelectedEvent('');
    setDateFilter(false);
  };

  const getHeaderWidth = () => {
    let width = 1590;
    if (filterByName || filterByDate) {
      width = 1140;
    }
    return width;
  };

  const getEventImageUrl = (eventName: string, imageUrl?: string) => {
    const imgObj =
      eventName &&
      eventImages.find((item: MediaResponse) => {
        return item.name === hash(eventName.replace(new RegExp('/', 'g'), ' '));
      });
    // image from media table
    if (imgObj && imgObj.s3Name) {
      return previewUrl.concat(imgObj.s3Name);
    }
    // image from ticketing system
    if (imageUrl) {
      return imageUrl;
    }

    return '';
  };

  const getDateForDisplay = (event: Event) => {
    const { showDate } = getEventDisplaySettings(eventDisplaySettingsList, event.name);

    let displayDate = '';
    if (showDate) {
      if (event.firstDate && event.lastDate) {
        displayDate = `${t('{{date, weekdayMonthDay}}', { date: new Date(event.firstDate) })} - ${t(
          '{{date, weekdayMonthDay}}',
          {
            date: new Date(event.lastDate)
          }
        )}`;
      } else if (event.date) {
        displayDate = t('{{date, weekdayMonthDay}}', { date: new Date(event.date) });
      }
    }

    return displayDate;
  };

  const getTimeForDisplay = (event: Event) => {
    const { showTime } = getEventDisplaySettings(eventDisplaySettingsList, event.name);

    return showTime && event.date ? t('{{date, time}}', { date: new Date(event.date) }) : '';
  };

  if (uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      <Grid className={classes.eventsViewOuterContainer}>
        <Grid container className={classes.eventsViewInnerContainer}>
          {/* Title */}
          <Grid container item direction="row" xl={6} lg={12} className={classes.eventsHeaderBlock}>
            <Grid item sx={{ width: getHeaderWidth() }} className={classes.eventsHeaderText}>
              <Box flexOverride="none" margin="0 0 5">
                <Typography variant="h2" sx={{ fontFamily: fonts?.primary }}>
                  {(messaging && messaging.header) || t('events.eventsMessagingHeader')}
                </Typography>
                <Typography fontSize={30} fontWeight={300} sx={{ fontFamily: fonts?.primary }}>
                  {(messaging && messaging.message) || t('events.eventsMessagingSubHeader')}
                </Typography>
              </Box>
            </Grid>
            <Grid item className={classes.eventsSearchInput}>
              <div className={classes.eventsSearchInputBox}>
                {filteredEvents.length > 0 && filterByName && (
                  <MuiBox className={classes.eventsNameButtonBox}>
                    <InputField
                      placeholder={t('events.searchByName')}
                      size="medium"
                      sx={{
                        width: '450px',
                        height: '68px',
                        fontSize: '26px'
                      }}
                      onClick={() => {
                        setShowNameSearchOverlay(true);
                      }}
                      value={searchScript}
                      startIcon={
                        <SvgIconButton
                          onClick={() => {
                            setShowNameSearchOverlay(true);
                          }}
                          appearance="transparent"
                          sx={{
                            boxShadow: 'none'
                          }}
                        >
                          <SvgIcon name="search" size={50} />
                        </SvgIconButton>
                      }
                      endIcon={
                        <SvgIconButton
                          appearance="transparent"
                          onClick={resetNameFilter}
                          sx={{
                            boxShadow: 'none',
                            opacity: pinOpacity,
                            pointerEvents: pinClearPointer
                          }}
                          customTransition={transformTransition}
                        >
                          <SvgIcon name="exit_circle" size={50} />
                        </SvgIconButton>
                      }
                    />
                  </MuiBox>
                )}
                {filteredEvents.length > 0 && filterByDate && (
                  <MuiBox className={classes.eventsDateButtonBox}>
                    <InputField
                      placeholder={t('events.searchByDate')}
                      size="medium"
                      sx={{
                        width: '450px',
                        height: '68px',
                        fontSize: '26px'
                      }}
                      value={dateTextForDisplay()}
                      onClick={() => {
                        setShowDateOverlay(true);
                      }}
                      startIcon={
                        <SvgIconButton
                          onClick={() => {
                            setShowDateOverlay(true);
                          }}
                          appearance="transparent"
                          sx={{
                            boxShadow: 'none'
                          }}
                        >
                          <SvgIcon name="calendar" size={50} />
                        </SvgIconButton>
                      }
                      endIcon={
                        <SvgIconButton
                          appearance="transparent"
                          onClick={() => {
                            resetDateFilter();
                          }}
                          sx={{
                            boxShadow: 'none',
                            opacity: dateOpacity,
                            pointerEvents: dateClearPointer
                          }}
                          customTransition={transformTransition}
                        >
                          <SvgIcon name="exit_circle" size={50} />
                        </SvgIconButton>
                      }
                    />
                  </MuiBox>
                )}
              </div>
            </Grid>
          </Grid>

          {/* This is displayed when there are no events loaded */}
          {!loading && !nameFilter && !dateFilter && filteredEvents.length === 0 && (
            <Grid container className={classes.eventsNoEventsBlock}>
              <MuiBox className={classes.eventsNoEventsImage}>
                <Img src={EmptyTicketIcon} width={207} height={180} />
              </MuiBox>
              <Typography fontSize={30} fontWeight={300}>
                {t('events.noEvents')}
              </Typography>
            </Grid>
          )}

          {/* This is displayed when there are no events after the filter */}
          {(nameFilter || dateFilter) && filteredEvents.length === 0 && (
            <div className={classes.eventsSearchNotFound}>
              <FlexBox direction="row" fullWidth align="center" justify="center" padding="7 0 5">
                <Img src={SearchIcon} width={180} height={180} />
              </FlexBox>
              <FlexBox fullWidth direction="row" justify="center" align="center">
                <Typography fontSize={30} fontWeight={300}>
                  {t('events.noMatchingResult')}
                </Typography>
              </FlexBox>
              <FlexBox fullWidth direction="row" justify="center" align="center">
                <Typography fontSize={24}>{t('events.performAnotherSearch')}</Typography>
              </FlexBox>
              <FlexBox fullWidth direction="row" justify="center" align="center" padding="5 0">
                <Button
                  onClick={() => {
                    resetFilters();
                  }}
                  customSize="medium"
                  sx={{ background: color.BLACK, borderWidth: '0px' }}
                  className={classes.eventsSearchButton}
                >
                  {t('events.clearSearch')}
                </Button>
              </FlexBox>
            </div>
          )}

          {/* List of event cards, wrapped in paginator. Paginates if there are more than 4 events */}
          {
            <Paginator
              items={filteredEvents}
              itemsPerPage={4}
              page={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            >
              {({ items }) => (
                <div className={classes.eventsCardContainer}>
                  {items.map((event, idx) => (
                    <MuiBox key={idx} className={classes.eventsCardBox}>
                      {/* Event Card */}
                      <Card
                        hasBorderRadius
                        raised
                        fullHeight
                        hasBorder={event.id === selectedEvent}
                        borderColor={palette?.primary}
                      >
                        {/* Hidden button to access seat selection demo */}
                        {configuration && configuration.name && configuration.name === 'KIS Technologies' && (
                          <HiddenButton onClick={() => onHiddenButtonClick(event.id)} />
                        )}
                        {/* Selects event name on click of card */}
                        <Card.ActionArea onClick={() => setSelectedEvent(event.id)}>
                          <Grid container className={classes.eventsCardContent}>
                            {todayEventFlag && event.date && isToday(new Date(event.date)) && (
                              <Card.Badge>{t('events.today')}</Card.Badge>
                            )}
                            <Grid item className={classes.eventsCardImage}>
                              <Img
                                src={[getEventImageUrl(event.name, event.imageUrl), `${previewUrl}default.png`]}
                                className={classes.eventsViewImage}
                                unloader={
                                  <img src={WillCallLogo} alt="Event Logo" className={classes.eventsDefaultImage} />
                                }
                              />
                            </Grid>
                            <Grid item>
                              <MuiBox className={classes.eventsHorizontalDivider}>
                                <Card.Divider />
                              </MuiBox>
                              <MuiBox className={classes.eventsVerticalDivider} />
                            </Grid>
                            <Grid container item className={classes.eventsCardText} direction="column">
                              <FlexBox direction="column" justify="center" align="start">
                                <Typography fontSize={28} fontWeight={400}>
                                  {event.alias || event.name}
                                </Typography>
                              </FlexBox>
                              <FlexBox direction="column" justify="between" align="start">
                                <Typography fontSize={24} fontWeight={300}>
                                  {getDateForDisplay(event) || <>&nbsp;</>}
                                </Typography>
                                <Typography fontSize={24} fontWeight={300}>
                                  {getTimeForDisplay(event) || <>&nbsp;</>}
                                </Typography>
                              </FlexBox>
                            </Grid>
                          </Grid>
                        </Card.ActionArea>
                      </Card>
                    </MuiBox>
                  ))}
                </div>
              )}
            </Paginator>
          }
        </Grid>
        {showNameSearchOverlay && (
          <DataEntryOverlay
            title={t('events.searchEvents')}
            description={t('events.typeEventName')}
            inputHint={searchScript ? searchScript : t('events.eventName')}
            inputType="keyboard"
            nextText={t('commonText.enter')}
            prevText={t('commonText.back')}
            onCancel={() => setShowNameSearchOverlay(false)}
            onConfirm={filterEventsByName}
            loading={loading}
          />
        )}

        {showDateOverlay && (
          <CalendarOverlay
            title={t('events.searchDates')}
            confirmText={t('commonText.confirm')}
            cancelText={t('commonText.cancel')}
            onCancel={() => setShowDateOverlay(false)}
            // onConfirm={() => setShowDateOverlay(true)}
            onConfirm={filterEventsByDate}
            loading={loading}
            calendarType="range"
          />
        )}
      </Grid>
      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        onNextClick={onNextPageClick}
        nextText={touchForward ? undefined : t('commonText.next')}
        isNextDisabled={!selectedEvent.length}
      />
    </>
  );
};

export default EventsView;
